import { GREETING_DESKTOP, vipHeading } from '@/constants/content'
import SearchFormProvider from '@/context/SearchFormContext'
import useBootstrapData from '@/hooks/useBootstrapData'
import useCityNameInFormHeadlineForSEM from '@/hooks/useCityNameInFormHeadlineForSEM'
import useClientSideSignedInData from '@/hooks/useClientSideSignedInData'
import useSEMSearchHeadline from '@/hooks/useSEMSearchHeadline'
import type { LOCATION_SEARCH_TYPE, ProductType } from '@/types'
import { Heading } from '@pcln/horizon'
import { SearchFormTabs, type SearchFormTabsItem } from '@pcln/horizon-search-form'
import { useMemo, useState } from 'react'
import ConditionalRender, { Original } from '../ConditionalRender'
import { PRODUCT_TABS, tabIconUrls } from '../SearchForm/constants'
import useHeadlineValues from '../SearchForm/customHooks/useHeadlineValues'
import useSearchFormTabState from '../SearchForm/customHooks/useSearchFormTabState'
import getProductCategory from './utils/getProductCategory'

export default function SearchFormNewHorizonTabs({
  currentTab,
  onFormChange,
}: {
  currentTab: ProductType
  onFormChange: (productType: ProductType) => void
}) {
  const [location, setLocation] = useState<LOCATION_SEARCH_TYPE | undefined>()
  const { activeTab, changeTab } = useSearchFormTabState({ initialTab: currentTab, onFormChange })

  const {
    customer: { firstName: firstNameBootstrap },
    signInStatus: { signedIn: serverSideSignedIn },
  } = useBootstrapData()

  const { firstName: clientSideFirstName, signedIn: clientSideSignedIn } = useClientSideSignedInData()

  const getFirstName = () => {
    if (serverSideSignedIn) return firstNameBootstrap
    if (clientSideSignedIn) return clientSideFirstName
    return undefined
  }

  const firstName = getFirstName()

  const prePopHeadline = useCityNameInFormHeadlineForSEM(location)
  const SEMHeadline = useSEMSearchHeadline()
  const headlineValues = useHeadlineValues(currentTab)

  const headlineWithName = firstName ? vipHeading.headlineWithName(firstName) : GREETING_DESKTOP
  const defaultHeadline = firstName ? headlineWithName : headlineValues.hotels.headline
  const hotelHeadline = prePopHeadline || SEMHeadline || defaultHeadline
  const updatedHeadlineValues = useMemo(
    () => ({
      ...headlineValues,
      hotels: {
        ...headlineValues.hotels,
        headline: hotelHeadline,
      },
    }),
    [hotelHeadline, headlineValues],
  )

  const headline = updatedHeadlineValues[activeTab]?.headline ?? ''

  const tabsContent = useMemo<ReadonlyArray<SearchFormTabsItem<ProductType>>>(
    () =>
      PRODUCT_TABS.map((productTab) => {
        const { component, bumper } = getProductCategory(
          productTab.id,
          updatedHeadlineValues,
          setLocation,
          true,
        )
        return {
          ...productTab,
          iconLeft: ({ className }: { className?: string }) => (
            // eslint-disable-next-line @next/next/no-img-element
            <img alt={productTab.id} src={tabIconUrls[productTab.id]} className={className} />
          ),
          panelContent: (
            <SearchFormProvider>
              <div className='flex flex-col'>
                <div className='p-3 md:p-4'>{component}</div>
                <ConditionalRender experimentCode='HP_SEARCH_FORM_WITHOUT_FOOTER_DESKTOP'>
                  <Original>
                    <div className='mt-4 w-full'>{bumper}</div>
                  </Original>
                </ConditionalRender>
              </div>
            </SearchFormProvider>
          ),
          value: productTab.id,
          label: productTab.tabText,
        }
      }),
    [updatedHeadlineValues],
  )

  return (
    <div className='w-full'>
      <Heading
        as='h1'
        textStyle='heading2'
        palette='primary'
        className='m-0 mx-4 px-0 pb-4 text-left text-heading2 max-md:text-heading3 md:text-heading2 min-[90rem]:mx-0 min-[90rem]:pr-[130px] min-[90rem]:text-heading1'
      >
        {headline}
      </Heading>
      <SearchFormTabs tabs={tabsContent} value={activeTab} onValueChange={changeTab} />
    </div>
  )
}
